import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Wind, Brain, Heart, Moon, Sun, ArrowLeft, Play, CheckCircle, Lightbulb, History } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const MeditationPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [started, setStarted] = useState(false);
  const [entries, setEntries] = useState([]);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/meditation`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const saveMeditation = async (meditation) => {
    try {
      const res = await axios.post(`${API}/meditation`, {
        meditation_type: meditation.id,
        duration: meditation.duration,
      }, { withCredentials: true });
      toast.success(t("meditation.completed"));
      if (user?.role === "patient") promptShare(res.data, "meditation");
      fetchEntries();
    } catch (e) { console.error(e); }
  };

  const MEDITATIONS = [
    {
      id: "breathing",
      name: t("meditation.breathing"),
      description: t("meditation.breathingDesc"),
      icon: Wind,
      duration: "5 " + t("meditation.minutes"),
      instructions: [
        t("meditation.breathing.inst1"), t("meditation.breathing.inst2"), t("meditation.breathing.inst3"),
        t("meditation.breathing.inst4"), t("meditation.breathing.inst5"), t("meditation.breathing.inst6"),
      ],
    },
    {
      id: "bodyScan",
      name: t("meditation.bodyScan"),
      description: t("meditation.bodyScanDesc"),
      icon: Brain,
      duration: "15 " + t("meditation.minutes"),
      instructions: [
        t("meditation.bodyScan.inst1"), t("meditation.bodyScan.inst2"), t("meditation.bodyScan.inst3"),
        t("meditation.bodyScan.inst4"), t("meditation.bodyScan.inst5"), t("meditation.bodyScan.inst6"),
      ],
    },
    {
      id: "gratitude",
      name: t("meditation.gratitudeMed"),
      description: t("meditation.gratitudeMedDesc"),
      icon: Heart,
      duration: "10 " + t("meditation.minutes"),
      instructions: [
        t("meditation.gratitudeMed.inst1"), t("meditation.gratitudeMed.inst2"), t("meditation.gratitudeMed.inst3"),
        t("meditation.gratitudeMed.inst4"), t("meditation.gratitudeMed.inst5"), t("meditation.gratitudeMed.inst6"),
      ],
    },
    {
      id: "calm",
      name: t("meditation.innerCalm"),
      description: t("meditation.innerCalmDesc"),
      icon: Sun,
      duration: "10 " + t("meditation.minutes"),
      instructions: [
        t("meditation.innerCalm.inst1"), t("meditation.innerCalm.inst2"), t("meditation.innerCalm.inst3"),
        t("meditation.innerCalm.inst4"), t("meditation.innerCalm.inst5"), t("meditation.innerCalm.inst6"),
      ],
    },
    {
      id: "sleep",
      name: t("meditation.sleepPrep"),
      description: t("meditation.sleepPrepDesc"),
      icon: Moon,
      duration: "15 " + t("meditation.minutes"),
      instructions: [
        t("meditation.sleepPrep.inst1"), t("meditation.sleepPrep.inst2"), t("meditation.sleepPrep.inst3"),
        t("meditation.sleepPrep.inst4"), t("meditation.sleepPrep.inst5"), t("meditation.sleepPrep.inst6"),
      ],
    },
  ];

  const TIPS = [
    { title: t("meditation.tipFindTime"), desc: t("meditation.tipFindTimeDesc") },
    { title: t("meditation.tipCreateSpace"), desc: t("meditation.tipCreateSpaceDesc") },
    { title: t("meditation.tipBePatient"), desc: t("meditation.tipBePatientDesc") },
  ];

  const closeMeditation = (save = false) => {
    if (save && selectedMeditation) saveMeditation(selectedMeditation);
    setSelectedMeditation(null);
    setCurrentStep(0);
    setStarted(false);
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="meditation-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("meditation.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("meditation.subtitle")}</p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} {t("meditation.sessionsCompleted")}
            </span>
          )}
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.meditation} />

        {selectedMeditation ? (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <selectedMeditation.icon className="w-5 h-5" />
                {selectedMeditation.name}
                <span className="text-sm font-normal text-muted-foreground ml-auto">
                  {selectedMeditation.duration}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground">{selectedMeditation.description}</p>

              {!started ? (
                <Button
                  onClick={() => setStarted(true)}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="start-meditation-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {t("meditation.startSession")}
                </Button>
              ) : (
                <>
                  <ol className="space-y-3">
                    {selectedMeditation.instructions.map((instruction, idx) => (
                      <li
                        key={idx}
                        className={`flex gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                          currentStep >= idx ? "bg-indigo-50" : "bg-muted/50"
                        }`}
                        onClick={() => setCurrentStep(idx)}
                      >
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${
                          currentStep >= idx ? "bg-indigo-500 text-white" : "bg-muted text-muted-foreground"
                        }`}>
                          {currentStep > idx ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                        </span>
                        <span className={currentStep >= idx ? "text-foreground" : "text-muted-foreground"}>
                          {instruction}
                        </span>
                      </li>
                    ))}
                  </ol>

                  <Button
                    onClick={() =>
                      currentStep < selectedMeditation.instructions.length - 1
                        ? setCurrentStep(currentStep + 1)
                        : closeMeditation(true)
                    }
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="next-meditation-step"
                  >
                    {currentStep < selectedMeditation.instructions.length - 1
                      ? t("compassion.nextStep")
                      : t("compassion.finishExercise")}
                  </Button>
                </>
              )}

              <Button variant="ghost" onClick={() => closeMeditation(false)}>
                {t("meditation.backToMeditations")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {MEDITATIONS.map((meditation) => (
                <Card
                  key={meditation.id}
                  className="card-hover cursor-pointer"
                  onClick={() => setSelectedMeditation(meditation)}
                  data-testid={`meditation-card-${meditation.id}`}
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">
                      <meditation.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-1">{meditation.name}</h3>
                    <p className="text-xs text-muted-foreground mb-2">{meditation.duration}</p>
                    <p className="text-sm text-muted-foreground">{meditation.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  {t("meditation.tips")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-4">
                  {TIPS.map((tip, idx) => (
                    <div key={idx} className="p-4 bg-secondary/50 rounded-lg">
                      <h4 className="font-medium text-sm mb-1">{tip.title}</h4>
                      <p className="text-xs text-muted-foreground">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default MeditationPage;
